import { CircleAlert, CircleCheck, FileText } from 'lucide-react'

import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { type IntakeKind, IntakeRowStatus } from '@/models/fillApplication'
import { DocumentPreviewLink } from './DocumentPreviewLink'
import { kindsSentence } from './intakeLabels'

interface ReadingRowProps {
  /** Makes the name open the file. */
  documentId?: string
  fileName: string
  /** What the Documents tab calls it: a name someone set, else the parsed title. */
  title?: string
  status: IntakeRowStatus
  kinds: IntakeKind[]
  /** Ran past its time budget: reads as failed, and stops holding back sending. */
  stale?: boolean
  /** Why it was stopped, when it was. */
  stopReason?: string
  fromCustomer?: boolean
}

function statusText({ status, kinds, stale, stopReason }: ReadingRowProps): string {
  if (stale) return 'Took too long to read. It no longer holds back sending.'
  switch (status) {
    case IntakeRowStatus.DONE:
      return kinds.length > 0 ? `Found: ${kindsSentence(kinds)}` : 'Read. Nothing Cai could use.'
    case IntakeRowStatus.FAILED:
      return "Couldn't read this file."
    case IntakeRowStatus.ABANDONED:
      return stopReason || 'Stopped.'
    default:
      return 'Reading…'
  }
}

export function ReadingRow(props: ReadingRowProps) {
  const { documentId, fileName, title, status, stale, fromCustomer } = props
  const reading = !stale && (status === IntakeRowStatus.PENDING || status === IntakeRowStatus.RUNNING)
  const failed = stale || status === IntakeRowStatus.FAILED
  const text = statusText(props)
  const name = title || fileName

  return (
    <li className="flex items-start gap-3 text-sm">
      {reading ? (
        <Spinner className="text-ink-400 mt-0.5 size-4 shrink-0" />
      ) : failed ? (
        <CircleAlert className="text-error mt-0.5 size-4 shrink-0" />
      ) : status === IntakeRowStatus.DONE ? (
        <CircleCheck className="text-accent-green mt-0.5 size-4 shrink-0" />
      ) : (
        <FileText className="text-ink-400 mt-0.5 size-4 shrink-0" />
      )}
      <div className="flex min-w-0 flex-col">
        {documentId ? (
          <DocumentPreviewLink documentId={documentId} fileName={fileName} title={name} className="truncate">
            {name}
          </DocumentPreviewLink>
        ) : (
          <span className="truncate font-medium">{name}</span>
        )}
        {title && title !== fileName && <span className="text-ink-500 truncate text-xs">{fileName}</span>}
        <span className={cn('text-ink-500', failed && 'text-error')}>
          {fromCustomer && "The customer's upload. "}
          {text}
        </span>
      </div>
    </li>
  )
}
